/**
 * appointmentChecklistContent.js
 *
 * Pure constants and helpers for the TOC Adult Autism appointment checklists.
 * These checklists are displayed in a dialog accessible from the breadcrumb row
 * on Developmental History (History + SSI) and Observation proformas.
 *
 * The content reflects The Owl Centre's clinical protocol. Items marked
 * "(If applicable)" or "(For adults)" always appear on the Adult Autism pathway
 * — the qualifiers are informational for the clinician, not conditional rendering.
 *
 * No React, no side effects — pure data and string matching.
 */

/**
 * Start-of-appointment checklist items. Shared across both the Developmental
 * History appointment and the Observation appointment.
 */
export const START_APPOINTMENT_ITEMS = [
  'Introduce self and The Owl Centre',
  'Consent to record',
  'Ask the client to state and confirm their name, preferred name, DOB, address and GP.',
  'Check current location',
  'Check who is with them, is anyone else in the room',
  '(For adults) Record an emergency contact',
  'Confidentiality / Safeguarding',
  'Consent for assessment',
  '(If applicable) Describe the assessment process and possible outcomes',
  'Describe the current appointment',
  'Check the camera position/view before starting',
  '(If applicable) Ensure suitability and accessibility for online ADOS',
];

/**
 * End-of-appointment checklist items. Item 1 differs by appointment type because
 * the risk tool used on The Owl Centre's NEST platform has a different name for
 * each appointment: "Risk Screen" for the Developmental History session and
 * "Risk Check-in" for the Observation session.
 *
 * @param {'developmentalHistory' | 'observation'} appointmentType
 * @returns {string[]}
 */
export function getEndAppointmentItems(appointmentType) {
  const riskLabel = appointmentType === 'observation'
    ? 'Complete the Risk Check-in (Observation) on NEST'
    : 'Complete the Risk Screen (Developmental History) on NEST';

  return [
    riskLabel,
    'Check outstanding questionnaires',
    'Inform next steps',
  ];
}

/**
 * Determines whether the appointment checklist feature should be shown for a
 * given organisation. Uses substring matching (same pattern as QbCheck gating
 * in workflowConfig.js) so branch variants like "The Owl Centre - Branch" still
 * match. Demo Org is included so staging/demo environments exercise the feature.
 *
 * Gate on the *patient's* organisation (header.organisation or
 * patientDataModel.organisation), NOT the signed-in user's claims.organisation.
 * This ensures Support staff viewing a TOC patient still see the checklist, and
 * an LMP patient never does — even though LMP shares assessmentWorkflowType
 * TOC_ADHD with The Owl Centre.
 *
 * @param {string|null|undefined} orgName
 * @returns {boolean}
 */
export function isTocAppointmentChecklistOrg(orgName) {
  if (!orgName) return false;
  return orgName.includes('The Owl Centre') || orgName.includes('Demo Org');
}

/**
 * Builds the location.state payload that triggers auto-open of the Start
 * Appointment checklist when landing on a proforma page from the overview card.
 *
 * Only DDI (developmentalDiagnosticInterview) and OBS (behaviouralObservation)
 * modules receive this state, and only when the clinician is starting or
 * resuming the appointment (Start / Continue). Completed modules whose card
 * shows Review do not auto-open — the checklist remains available manually.
 *
 * @param {object} options
 * @param {boolean} options.enabled - Whether the org qualifies for checklists
 * @param {string} options.moduleKey - The overview module key
 * @param {'Start'|'Continue'|'Review'} [options.actionLabel] - Card action label
 * @returns {{ openAppointmentChecklist: 'start' } | undefined}
 */
export function getAppointmentChecklistActionState({ enabled, moduleKey, actionLabel }) {
  if (!enabled) return undefined;
  if (actionLabel === 'Review') return undefined;
  if (
    moduleKey === 'developmentalDiagnosticInterview' ||
    moduleKey === 'behaviouralObservation'
  ) {
    return { openAppointmentChecklist: 'start' };
  }
  return undefined;
}
